import { useEffect, useState } from "react";
import { InputGroup, InputGroupAddon, InputGroupInput } from "@/components/ui/input-group";
import { Spinner } from "@/components/ui/spinner";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Label } from "@/components/ui/label";
import {
  Sparkles,
  Save,
  GlobeIcon,
  LinkIcon,
  AtSignIcon,
} from "lucide-react";
import tiktok from "@/assets/tiktok.svg";
import instagram from "@/assets/instagram.svg";
import youtube from "@/assets/youtube.svg";
import { useAuth, useUser } from "@clerk/react";
import { useOrganizationSettingsStore } from "@/stores";
import { toast } from "sonner";
import {
  Frame,
  FrameHeader,
  FrameTitle,
  FramePanel,
  FrameFooter,
} from "@/components/ui/frame";

interface BrandValues {
  websiteUrl: string;
  affiliatePageLink: string;
  instagramHandle: string;
  tiktokHandle: string;
  youtubeHandle: string;
  brandInfo: string;
}

// The brand's social handles (organizations.instagram_handle / tiktok_handle /
// youtube_handle): the analytics cron's brand-mention scan reads them.
const HANDLE_FIELDS = [
  { key: "instagramHandle", label: "Instagram Handle", logo: instagram, logoClassName: "size-3.5" },
  { key: "tiktokHandle", label: "TikTok Handle", logo: tiktok, logoClassName: "size-3.5" },
  { key: "youtubeHandle", label: "YouTube Handle", logo: youtube, logoClassName: "h-3 shrink-0" },
] as const;

export function BrandSetupSection() {
  const {
    organizationInfo,
    saveOrganizationSetting,
    generateFaqFromWebsite,
    isLoading,
  } = useOrganizationSettingsStore();
  const { orgId, getToken } = useAuth();
  const { user } = useUser();

  const [values, setValues] = useState<BrandValues>({
    websiteUrl: "",
    affiliatePageLink: "",
    instagramHandle: "",
    tiktokHandle: "",
    youtubeHandle: "",
    brandInfo: "",
  });
  const [initialValues, setInitialValues] = useState<BrandValues>({
    websiteUrl: "",
    affiliatePageLink: "",
    instagramHandle: "",
    tiktokHandle: "",
    youtubeHandle: "",
    brandInfo: "",
  });
  const [saving, setSaving] = useState(false);
  const [generatingFaq, setGeneratingFaq] = useState(false);

  useEffect(() => {
    const newValues: BrandValues = {
      websiteUrl: organizationInfo?.website_url || "",
      affiliatePageLink: organizationInfo?.affiliate_page_link || "",
      instagramHandle: organizationInfo?.instagram_handle || "",
      tiktokHandle: organizationInfo?.tiktok_handle || "",
      youtubeHandle: organizationInfo?.youtube_handle || "",
      brandInfo: organizationInfo?.faq || "",
    };
    setValues(newValues);
    setInitialValues(newValues);
  }, [organizationInfo]);

  const hasChanges = (key: keyof BrandValues) =>
    values[key] !== initialValues[key];

  const hasAnyLinkChanges =
    hasChanges("websiteUrl") ||
    hasChanges("affiliatePageLink") ||
    hasChanges("instagramHandle") ||
    hasChanges("tiktokHandle") ||
    hasChanges("youtubeHandle");

  const fieldApiKeyMap: Record<string, string> = {
    websiteUrl: "organization_website_url",
    affiliatePageLink: "organization_affiliate_page_link",
    instagramHandle: "organization_instagram_handle",
    tiktokHandle: "organization_tiktok_handle",
    youtubeHandle: "organization_youtube_handle",
    brandInfo: "organization_faq",
  };

  const handleSaveLinks = async () => {
    if (!orgId || !user?.id) return;
    setSaving(true);
    try {
      const token = await getToken();
      if (!token) return;

      const fieldsToSave = (
        [
          "websiteUrl",
          "affiliatePageLink",
          "instagramHandle",
          "tiktokHandle",
          "youtubeHandle",
        ] as const
      ).filter((key) => hasChanges(key));

      for (const key of fieldsToSave) {
        const success = await saveOrganizationSetting(
          orgId,
          user.id,
          fieldApiKeyMap[key],
          values[key],
          token,
        );
        if (success) {
          setInitialValues((prev) => ({ ...prev, [key]: values[key] }));
        } else {
          toast.error(`Failed to save ${key}`);
        }
      }
      toast.success("Brand links saved successfully");
    } catch {
      toast.error("Failed to save brand links");
    } finally {
      setSaving(false);
    }
  };

  const handleSaveBrandInfo = async () => {
    if (!orgId || !user?.id) return;
    setSaving(true);
    try {
      const token = await getToken();
      if (!token) return;
      const success = await saveOrganizationSetting(
        orgId,
        user.id,
        "organization_faq",
        values.brandInfo,
        token,
      );
      if (success) {
        setInitialValues((prev) => ({ ...prev, brandInfo: values.brandInfo }));
        toast.success("Brand Information saved successfully");
      } else {
        toast.error("Failed to save Brand Information");
      }
    } catch {
      toast.error("Failed to save Brand Information");
    } finally {
      setSaving(false);
    }
  };

  const handleGenerateFaq = async () => {
    if (!orgId || !user?.id || !values.websiteUrl?.trim()) {
      toast.error("Please enter a website URL");
      return;
    }
    setGeneratingFaq(true);
    try {
      const token = await getToken();
      if (!token) return;
      const result = await generateFaqFromWebsite(
        orgId,
        user.id,
        values.affiliatePageLink?.trim() || "",
        values.websiteUrl.trim(),
        token,
      );
      if (result.success && result.faq_content) {
        setValues((prev) => ({ ...prev, brandInfo: result.faq_content || "" }));
        setInitialValues((prev) => ({
          ...prev,
          brandInfo: result.faq_content || "",
        }));
        toast.success("FAQ content generated successfully");
      } else {
        toast.error("Failed to generate FAQ. Please check the website URL.");
      }
    } catch {
      toast.error("Failed to generate FAQ from website");
    } finally {
      setGeneratingFaq(false);
    }
  };

  return (
    <div className="space-y-8">
      <Frame>
        <FrameHeader>
          <FrameTitle>Smart Brand Setup</FrameTitle>
        </FrameHeader>
        <FramePanel>
          <div className="flex lg:flex-row flex-col gap-5">
            <div className="flex flex-col gap-2 w-full">
              <Label>
                <GlobeIcon className="size-3.5" /> Website URL
              </Label>
              <Input
                placeholder="https://your-website.com"
                value={values.websiteUrl}
                onChange={(e) =>
                  setValues((prev) => ({
                    ...prev,
                    websiteUrl: e.target.value,
                  }))
                }
              />
            </div>
            <div className="flex flex-col gap-2 w-full">
              <Label>
                <LinkIcon className="size-3.5" /> Affiliate Page URL
              </Label>
              <Input
                placeholder="https://your-website.com/affiliates"
                value={values.affiliatePageLink}
                onChange={(e) =>
                  setValues((prev) => ({
                    ...prev,
                    affiliatePageLink: e.target.value,
                  }))
                }
              />
            </div>
          </div>

          <div className="flex xl:flex-row flex-col gap-5 mt-5">
            {HANDLE_FIELDS.map(({ key, label, logo, logoClassName }) => (
              <div key={key} className="flex flex-col gap-2 w-full">
                <Label>
                  <img src={logo} alt="" className={`inline-block ${logoClassName}`} /> {label}
                </Label>
                <InputGroup>
                  <InputGroupInput
                    placeholder="yourhandle"
                    className="placeholder:text-sm"
                    value={values[key]}
                    onChange={(e) =>
                      setValues((prev) => ({ ...prev, [key]: e.target.value }))
                    }
                  />
                  <InputGroupAddon>
                    <AtSignIcon className="size-3.5" />
                  </InputGroupAddon>
                </InputGroup>
              </div>
            ))}
          </div>
        </FramePanel>
        <FrameFooter className="flex items-center justify-between">
          <div className="flex gap-2 ml-auto">
            {hasAnyLinkChanges && (
              <Button
                variant="secondary"
                className="animate-in fade-in-0 slide-in-from-right-2 duration-200"
                size="sm"
                onClick={handleSaveLinks}
                disabled={saving}
              >
                {saving ? <Spinner /> : <Save />}
                Save
              </Button>
            )}
            <Button
              onClick={handleGenerateFaq}
              size="sm"
              disabled={generatingFaq || !values.websiteUrl?.trim()}
            >
              {generatingFaq ? (
                <Spinner />
              ) : (
                <Sparkles />
              )}
              Generate FAQ
            </Button>
          </div>
        </FrameFooter>
      </Frame>

      <Frame>
        <FrameHeader>
          <FrameTitle>Brand Information - FAQ</FrameTitle>
        </FrameHeader>
        {isLoading && !values.brandInfo ? (
          <FramePanel>
            <Skeleton className="h-[300px]" />
          </FramePanel>
        ) : (
          <Textarea
            variant="panel"
            className="min-h-[300px]"
            placeholder="Enter brand information here..."
            value={values.brandInfo}
            onChange={(e) =>
              setValues((prev) => ({ ...prev, brandInfo: e.target.value }))
            }
            onBlur={handleSaveBrandInfo}
          />
        )}
        {hasChanges("brandInfo") && (
          <FrameFooter className="flex justify-end">
              <Button
                onClick={handleSaveBrandInfo}
                disabled={saving}
                size="sm"
                className="animate-in fade-in-0 slide-in-from-left-2 duration-200"
              >
                {saving ? <Spinner /> : <Save />}
                Save
              </Button>
          </FrameFooter>
        )}
      </Frame>
    </div>
  );
}
